import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TableContainer from "../../Components/Common/TableContainer";
import {
  Col,
  Row,
  UncontrolledTooltip,
  Card,
  CardBody,
  Badge,
  Button,
} from "reactstrap";
import Spinners from "Components/Common/Spinner";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import { AppRoutes } from "../../constants";
import {
  useDeleteProductTransfer,
  useGetProductTransfers,
} from "hooks/useProductTransfer";
import { capitalize } from "lodash";
import UpdateProductTransferStatus from "./components/UpdateProductTransferStatus";
import { RootState } from "slices";
import { useSelector } from "react-redux";
import { getStatusColor } from "utils";

function ProductTransfers() {
  document.title = "Papermap | Product Transfer";

  const selectedBranch = useSelector(
    (state: RootState) => state.branch?.currentBranch
  );

  const { mutate } = useDeleteProductTransfer();
  const [modal1, setModal1] = useState<boolean>(false);
  const [resource, setResource] = useState<any>(null);
  const [editDetails, setEditDetails] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const toggleEditModal = (data) => {
    setEditDetails(data);
    setShowEditModal(!showEditModal);
  };

  const navigate = useNavigate();

  const { data, isLoading } = useGetProductTransfers(selectedBranch?.id);

  const toggleViewModal = useCallback(
    (data: any) => {
      setModal1(!modal1);
      setEditDetails(data);
    },
    [modal1]
  );

  // delete resource
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const onClickDelete = (item: any) => {
    setResource(item);
    setDeleteModal(true);
  };

  const handleDeleteOrder = () => {
    if (resource.id) {
      mutate(resource.id);
      setDeleteModal(false);
    }
  };

  const handleEditClick = (productId: number) => {
    console.log(productId); // Check if the ID is logged correctly
    navigate(`/edit-product/${productId}`); // Navigate to the edit product page with the product ID
  };

  const handleAddProductClick = () => {
    navigate(AppRoutes.ADD_PRODUCT_TRANSFER);
  };

  // const handleViewProductTransfer = (productId: number) => {
  //   console.log(productId);
  //   navigate(`/view-product/${productId}`); // Navigate to the edit product page with the product ID
  // };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Source product",
        accessorKey: "from_product",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <div>{cellProps.row.original?.from_product?.name}</div>
        ),
      },
      {
        header: "Target product",
        accessorKey: "to_product",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <div>{cellProps.row.original?.to_product?.name}</div>
        ),
      },
      {
        header: "From",
        accessorKey: "from_branch",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => (
          <div>{cellProps.row.original?.from_branch?.name}</div>
        ),
      },
      {
        header: "To",
        accessorKey: "to_branch",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => (
          <div>{cellProps.row.original?.to_branch?.name}</div>
        ),
      },

      {
        header: "Status",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          const status = cellProps.row.original.status;
          return (
            <Badge
              className={`font-size-12 badge-soft-${getStatusColor(status)}`}
            >
              {capitalize(cellProps.row.original?.status)}
            </Badge>
          );
        },
      },
      {
        header: "Update Status",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <Button
            type="button"
            color="soft-info"
            className="btn-sm btn-rounded "
            onClick={() => toggleEditModal(cellProps.cell.row.original)}
          >
            Update
          </Button>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => {
          return (
            <>
              <ul className="list-unstyled hstack gap-1 mb-0">
                <li id="viewtooltip">
                  <Link
                    to={`/view-product-transfer/${cellProps.row.original.id}`}
                    className="btn btn-sm btn-soft-primary"
                  >
                    <i className="mdi mdi-eye-outline"></i>
                    <UncontrolledTooltip placement="top" target="viewtooltip">
                      View
                    </UncontrolledTooltip>
                  </Link>
                </li>

                <li id="edittooltip">
                  <Link
                    className="btn btn-sm btn-soft-info"
                    to={`/edit-product-transfer/${cellProps.row.original.id}`} // Directly navigate using the 'to' prop
                  >
                    <i className="mdi mdi-pencil-outline" />
                    <UncontrolledTooltip placement="top" target="edittooltip">
                      Edit
                    </UncontrolledTooltip>
                  </Link>
                </li>

                {cellProps.row.original.status === "success" ? null : (
                  <li id="deleteTooltip">
                    <Link
                      to="#"
                      className="btn btn-sm btn-soft-danger"
                      onClick={() => onClickDelete(cellProps.row.original)}
                    >
                      <i className="mdi mdi-delete" />
                      <UncontrolledTooltip
                        placement="top"
                        target="deleteTooltip"
                      >
                        Delete
                      </UncontrolledTooltip>
                    </Link>
                  </li>
                )}
              </ul>
            </>
          );
        },
      },
    ],
    [handleEditClick, toggleViewModal]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Product Transfers"
            breadcrumbItem="View Product Transfers"
          />
          {isLoading ? (
            <Spinners />
          ) : (
            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <TableContainer
                      columns={columns}
                      data={data || []}
                      isGlobalFilter={true}
                      isAddButton={true}
                      isCustomPageSize={true}
                      handleUserClick={handleAddProductClick}
                      isPagination={true}
                      SearchPlaceholder="Search product transfers..."
                      buttonClass="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2 addOrder-modal"
                      buttonName=" Add New Product Transfer"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                      theadClass="table-light"
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
      <UpdateProductTransferStatus
        isOpen={showEditModal}
        toggle={toggleEditModal}
        editDetails={editDetails}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      />
    </React.Fragment>
  );
}

export default ProductTransfers;
